import { UTCDateMini } from '@date-fns/utc';
import { addDays, endOfDay, endOfMinute, startOfDay, startOfISOWeek, subHours } from 'date-fns';

import { Timezone } from 'shared/types/dateTime';

export enum PeriodType {
  Last24Hours = 'last_24h',
  Last7Days = 'last_7d',
  ThisWeek = 'this_week',
  Last30Days = 'last_30d',
  Last90Days = 'last_90d',
  Custom = 'custom',
}

const PERIOD_TYPE_VALUES = new Set<string>(Object.values(PeriodType));

export const getPeriodTypeFromCode = (code: string): PeriodType | undefined =>
  PERIOD_TYPE_VALUES.has(code) ? (code as PeriodType) : undefined;

export type PresetPeriodType = Exclude<PeriodType, PeriodType.Custom>;

export const isPresetPeriod = (period: PeriodType): period is PresetPeriodType => period !== PeriodType.Custom;

const getNow = (timezone: Timezone) => (timezone === Timezone.Local ? new Date() : new UTCDateMini(new Date()));

const periodStart = (days: number, timezone: Timezone) => addDays(startOfDay(getNow(timezone)), days).toISOString();

const periodEnd = (days: number, timezone: Timezone) => addDays(endOfDay(getNow(timezone)), days).toISOString();

const weekStart = (timezone: Timezone) => startOfISOWeek(getNow(timezone)).toISOString();

export const getPeriodForFilter = (period: PresetPeriodType, timezone: Timezone = Timezone.Local): [string, string] => {
  switch (period) {
    case PeriodType.Last24Hours: {
      const nowMinute = endOfMinute(getNow(timezone));
      return [subHours(nowMinute, 24).toISOString(), nowMinute.toISOString()];
    }
    case PeriodType.Last7Days:
      return [periodStart(-6, timezone), periodEnd(0, timezone)];
    case PeriodType.ThisWeek:
      return [weekStart(timezone), periodEnd(0, timezone)];
    case PeriodType.Last30Days:
      return [periodStart(-29, timezone), periodEnd(0, timezone)];
    case PeriodType.Last90Days:
      return [periodStart(-89, timezone), periodEnd(0, timezone)];
  }
};

export const getPresetPeriodRange = (code?: string, timezone: Timezone = Timezone.Local): [string, string] | null => {
  const periodType = code ? getPeriodTypeFromCode(code) : undefined;
  return periodType && isPresetPeriod(periodType) ? getPeriodForFilter(periodType, timezone) : null;
};

type ResolvePeriodParams = {
  period?: string;
  date_from?: string;
  date_to?: string;
};

type ResolvedPeriod = {
  period: string;
  date_from: string;
  date_to: string;
};

export const resolvePeriodFromParams = (
  params: ResolvePeriodParams,
  defaultPeriod: PresetPeriodType = PeriodType.Last30Days,
  timezone: Timezone = Timezone.Local,
): ResolvedPeriod => {
  const { period, date_from, date_to } = params;

  if (period) {
    const periodType = getPeriodTypeFromCode(period);
    if (periodType && isPresetPeriod(periodType)) {
      const [from, to] = getPeriodForFilter(periodType, timezone);
      return { period: periodType, date_from: from, date_to: to };
    }
  }

  if (date_from && date_to) {
    return { period: PeriodType.Custom, date_from, date_to };
  }

  const [from, to] = getPeriodForFilter(defaultPeriod, timezone);
  return { period: defaultPeriod, date_from: from, date_to: to };
};
